import { Button, Flex, Input, InputGroup, InputLeftElement, TableContainer, Tooltip, IconButton, useToast, Badge, HStack, Select } from "@chakra-ui/react";
import { IconEdit, IconPlus, IconDownload, IconHourglassHigh, IconFileTypeDocx, IconFileTypeDoc } from "@tabler/icons-react";
import { FaArrowsSpin } from "react-icons/fa6";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { useEffect } from "react";
import { CreateOrEditReportModal } from "./CreateOrEditReportModal";
import { SectionHeader } from "../../../components/SectionHeader";
import { useCreateRolloverReport, useGetReportsByClientId } from "../../../api/reports/reportQueries";
import { ReportByClientId as Report, ReportByClientId } from "../../../api/reports/reportApi";
import { generateReportAsync, downloadReportAsync } from "../../../api/reports/reportApi";
import { useGetJurisdictions } from "../../../api/jurisdictions/jurisdictionQueries";
import { TableRowProps } from "../../../utils/types";
import { useGetEntitiesByClientId } from "../../../api/entities/entityQueries";
import { useGetReportTransaction } from "../../../api/reportTransactions/reportTransactionQueries";

interface Props {
    clientId?: string;
    reportId?: string;
}

// Helper component to check if report has transactions
const ReportActionsCell: FunctionComponent<{
    report: Report;
    generatingReportId: string | null;
    onGenerate: (report: Report) => void;
    onEdit: (report: Report) => void;
    onDownload: (report: Report) => void;
    refetchReports: () => void;
}> = ({ report, generatingReportId, onGenerate, onEdit, onDownload, refetchReports }) => {
    const { data: reportTransaction } = useGetReportTransaction(report.id);
    const generatedReport = report.generatedReport;
    const isGenerating = generatingReportId === report.id;
    const [isCreatingRollover, setIsCreatingRollover] = useState(false);
    const isCompleted = generatedReport?.status === "Completed";
    const hasTransactions = reportTransaction?.transactionIds && reportTransaction.transactionIds.length > 0;
    const { mutateAsync: createRolloverReport } = useCreateRolloverReport();

    return (
        <HStack spacing={2}>
            <Tooltip shouldWrapChildren label="Edit Report">
                <IconButton
                    icon={<IconEdit />}
                    aria-label="Edit Report"
                    variant="outline"
                    size="sm"
                    onClick={evt => {
                        evt.stopPropagation();
                        onEdit(report);
                    }}
                />
            </Tooltip>

            {/* Rollover Report Button: Disabled if no transactions, with explanatory tooltip */}
            <Tooltip
                shouldWrapChildren
                label={
                    hasTransactions
                        ? "Create Rollover Report"
                        : "Cannot create rollover report: No transactions are linked to this report. Please select transactions first."
                }
            >
                <IconButton
                    icon={<FaArrowsSpin />}
                    aria-label="Create Rollover Report"
                    variant="outline"
                    size="sm"
                    colorScheme={hasTransactions ? "yellow" : "gray"}
                    isLoading={isCreatingRollover}
                    isDisabled={!hasTransactions}
                    onClick={async evt => {
                        evt.stopPropagation();
                        setIsCreatingRollover(true);
                        try {
                            await createRolloverReport(report.id);
                            await refetchReports();
                        } finally {
                            setIsCreatingRollover(false);
                        }
                    }}
                />
            </Tooltip>

            {/* Generate Report Button: Disabled if no transactions, with explanatory tooltip */}
            <Tooltip
                shouldWrapChildren
                label={
                    hasTransactions ? "Generate Report" : "Cannot generate report: No transactions are linked to this report. Please select transactions first."
                }
            >
                <IconButton
                    icon={<IconFileTypeDoc />}
                    aria-label="Generate Report"
                    variant="outline"
                    size="sm"
                    colorScheme={hasTransactions ? "blue" : "gray"}
                    isLoading={isGenerating}
                    isDisabled={!hasTransactions || generatedReport?.status === "Processing" || generatedReport?.status === "Queued"}
                    onClick={evt => {
                        evt.stopPropagation();
                        onGenerate(report);
                    }}
                />
            </Tooltip>

            {isCompleted && (
                <Tooltip shouldWrapChildren label="Download Report">
                    <IconButton
                        icon={<IconDownload />}
                        aria-label="Download Report"
                        variant="outline"
                        size="sm"
                        colorScheme="green"
                        onClick={evt => {
                            evt.stopPropagation();
                            onDownload(report);
                        }}
                    />
                </Tooltip>
            )}
        </HStack>
    );
};

export const ReportPanel: FunctionComponent<Props> = ({ clientId, reportId }) => {
    const [showCreateOrEditReportDialog, setShowCreateOrEditReportDialog] = useState(false);
    const [reportData, setReportData] = useState<Report>();
    const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);
    const toast = useToast();

    // Fetch all required data from the database
    const { data: reports = [], isLoading: reportsLoading, refetch: refetchReports } = useGetReportsByClientId(clientId);
    const { data: entities = [] } = useGetEntitiesByClientId(clientId);
    const { data: jurisdictions = [] } = useGetJurisdictions();

    // Polling for report status updates
    useEffect(() => {
        // Check if any report is in Processing state
        const hasActiveReport = reports.some((r: Report) => r.generatedReport?.status === "Processing" || r.generatedReport?.status === "Queued");
        if (!hasActiveReport) return;
        const interval = setInterval(() => {
            refetchReports();
        }, 5000); // Poll every 5 seconds
        return () => clearInterval(interval);
    }, [reports, refetchReports]);

    const columnHelper = createColumnHelper<Report>();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchYear, setSearchYear] = useState<string>("");

    const defaultReportName = useMemo(() => {
        return reportId ? reports.find((r: Report) => r.id === reportId)?.name || "" : "";
    }, [reportId, reports]);

    const filteredData = useMemo(() => {
        // Sort reports by createdDate descending (most recent first)
        const sortedReports = (reports ?? []).slice().sort((a: ReportByClientId, b: ReportByClientId) => {
            const dateA = new Date(a.createdDate ?? 0).getTime();
            const dateB = new Date(b.createdDate ?? 0).getTime();
            return dateB - dateA;
        });

        if (searchTerm.trim() === "" && searchYear.trim() === "") {
            return sortedReports;
        } else {
            const value = searchTerm.trim().toLowerCase();
            const yearFilteredReports = sortedReports.filter((c: Report) => c.year?.toLowerCase().includes(searchYear.toLowerCase()));
            return yearFilteredReports.filter(
                (c: Report) =>
                    c.jurisdictionName?.toLowerCase().includes(value) ||
                    c.leadEntityName?.toLowerCase().includes(value) ||
                    c.type?.toLowerCase().includes(value) ||
                    c.generatedReport?.status?.toLowerCase().includes(value) ||
                    c.createdDate?.toLowerCase().includes(value),
            );
        }
    }, [reports, searchTerm, searchYear]);

    const isLoading = reportsLoading;

    function enableEdit(report: Report) {
        setReportData(report);
        setShowCreateOrEditReportDialog(true);
    }

    function closeCreateOrEditReportDialog() {
        setReportData(undefined);
        setShowCreateOrEditReportDialog(false);
        refetchReports();
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSearchTerm(value);
    }

    async function generateReport(report: Report) {
        setGeneratingReportId(report.id);

        try {
            toast({
                title: "Generating Report",
                description: `Starting generation for ${report.jurisdictionName}`,
                status: "info",
                duration: 3000,
                isClosable: true,
            });

            await generateReportAsync(report.id);

            // Refetch reports to get updated status
            await refetchReports();
        } catch (error) {
            console.error("Error generating report:", error);
            toast({
                title: "Generation Failed",
                description: error instanceof Error ? error.message : "Failed to generate report",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setGeneratingReportId(null);
        }
    }

    async function downloadReport(report: Report) {
        try {
            const response = await downloadReportAsync(report.id);

            // Get the filename from the response headers or use a default
            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch?.[1] || `Transfer_Pricing_Report_${report.leadEntityName}.docx`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Download Complete",
                description: `Report for ${report.jurisdictionName} has been downloaded`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading report:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download report",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Reports">
                <InputGroup flex="0 1 35rem">
                    <Select
                        placeholder="Select year"
                        onChange={e => {
                            setSearchYear(e.target.value);
                        }}
                    >
                        {Array.from(new Set((Array.isArray(reports) ? reports : []).map(report => report.year))).map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </Select>
                </InputGroup>
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input defaultValue={defaultReportName} placeholder="Search Reports" onChange={handleFilter} />
                </InputGroup>
                <Tooltip
                    label="Entities must be added before you can create a report"
                    isDisabled={!!entities && entities.length > 0}
                    shouldWrapChildren
                    placement="top"
                >
                    <Button
                        leftIcon={<IconPlus />}
                        variant="primary"
                        flexShrink="0"
                        onClick={() => setShowCreateOrEditReportDialog(true)}
                        isDisabled={!entities || entities.length === 0}
                    >
                        Create Report
                    </Button>
                </Tooltip>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                <DataTable
                    enableFilters={false}
                    tableProps={{ size: "sm" }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No reports found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("leadEntityName", {
                            header: "Lead Entity",
                            cell: (props: TableRowProps<Report>) => props.getValue(),
                        }),
                        columnHelper.accessor("year", {
                            header: "Year",
                            cell: (props: TableRowProps<Report>) => props.getValue(),
                        }),
                        columnHelper.accessor("jurisdictionName", {
                            header: "Jurisdiction",
                            cell: (props: TableRowProps<Report>) => props.getValue(),
                        }),
                        columnHelper.accessor("createdDate", {
                            header: "Date Created",
                            /**
                             * Formats the created date to a more readable form.
                             * @param props TableRowProps<Report>
                             * @returns Formatted date string or original value if invalid.
                             */
                            cell: (props: TableRowProps<Report>) => {
                                const value = props.getValue();
                                if (!value) return "";
                                // Parse as UTC and convert to local time
                                const utcDate = value.toString().endsWith("Z") ? value : value + "Z";
                                const date = new Date(utcDate.toString());
                                if (isNaN(date.getTime())) return value;
                                // Example: Jan 1, 2024, 10:30 AM
                                return date.toLocaleString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                });
                            },
                        }),
                        columnHelper.accessor(row => row.generatedReport?.status, {
                            id: "status",
                            header: "Status",
                            cell: (props: TableRowProps<Report>) => {
                                const status = props.getValue() as string;
                                const reportId = props.row.original.id;
                                const generatedReport = props.row.original.generatedReport;
                                const isGenerating =
                                    generatingReportId === reportId || generatedReport?.status === "Processing" || generatedReport?.status === "Queued";

                                console.log(
                                    "We may need to use 'isGenerating' later. isGenerating:",
                                    isGenerating,
                                    "reportId:",
                                    reportId,
                                    "generatedReport:",
                                    generatedReport,
                                );

                                // Determine the primary status to display based on generated report status
                                let displayStatus = status;
                                let colorScheme = "gray";
                                let icon = null;

                                if (generatedReport) {
                                    // Use the actual generated report status from the API
                                    switch (generatedReport.status) {
                                        case "Processing":
                                            displayStatus = "Generating...";
                                            colorScheme = "yellow";
                                            icon = <IconFileTypeDocx size={14} />;
                                            break;
                                        case "Queued":
                                            displayStatus = "Generation Requested";
                                            colorScheme = "orange";
                                            icon = <IconHourglassHigh size={14} />;
                                            break;
                                        case "Completed":
                                            displayStatus = "Ready for Download";
                                            colorScheme = "green";
                                            icon = <IconFileTypeDocx size={14} />;
                                            break;
                                        case "Failed":
                                            displayStatus = "Generation Failed";
                                            colorScheme = "red";
                                            icon = <IconFileTypeDoc size={14} />;
                                            break;
                                        default:
                                            // Fallback to workflow status
                                            displayStatus = "New";
                                            colorScheme = status === "New" ? "blue" : "gray";
                                    }
                                } else {
                                    displayStatus = "New";
                                    colorScheme = "blue";
                                }

                                return (
                                    <Badge colorScheme={colorScheme} variant="subtle" size="sm">
                                        <HStack spacing={1}>
                                            {icon}
                                            <span>{displayStatus}</span>
                                        </HStack>
                                    </Badge>
                                );
                            },
                        }),
                        {
                            id: "Actions",
                            header: "Actions",
                            cell: props => {
                                const row = props.row.original;
                                return (
                                    <ReportActionsCell
                                        report={row}
                                        generatingReportId={generatingReportId}
                                        onGenerate={generateReport}
                                        onEdit={enableEdit}
                                        onDownload={downloadReport}
                                        refetchReports={refetchReports}
                                    />
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <Flex>
                <CreateOrEditReportModal
                    show={showCreateOrEditReportDialog}
                    onClose={closeCreateOrEditReportDialog}
                    reportData={reportData || undefined}
                    reportNames={reports ?? []}
                    isEdit={!!reportData}
                    jurisdictions={jurisdictions || []}
                    entities={entities || []}
                    clientId={clientId || ""}
                />
            </Flex>
        </Flex>
    );
};
